import { useEffect, useCallback } from 'react';
import { Template } from '../../../mocks/websiteTemplates';

interface TemplatePreviewModalProps {
  template: Template;
  onClose: () => void;
}

const CATEGORY_COLORS: Record<string, string> = {
  'E-commerce': 'from-emerald-500 to-teal-500',
  'SaaS & Tech': 'from-violet-500 to-indigo-500',
  'Portfolio & Creative': 'from-pink-500 to-rose-500',
  'Restaurant & Food': 'from-orange-500 to-amber-500',
  'Health & Wellness': 'from-green-500 to-emerald-500',
  'Real Estate': 'from-cyan-500 to-sky-500',
  'Education': 'from-amber-500 to-yellow-500',
  'Non-Profit': 'from-red-500 to-rose-500',
  'Fashion & Beauty': 'from-fuchsia-500 to-pink-500',
  'Travel & Hospitality': 'from-sky-500 to-cyan-500',
  'Finance & Legal': 'from-slate-600 to-gray-600',
  'Entertainment & Media': 'from-purple-500 to-violet-500',
};

const TAG_STYLES: Record<string, string> = {
  'Minimal': 'bg-gray-100 text-gray-700',
  'Elegant': 'bg-rose-50 text-rose-700',
  'Bold': 'bg-orange-50 text-orange-700',
  'Modern': 'bg-violet-50 text-violet-700',
  'Dark': 'bg-gray-800 text-gray-200',
  'Colorful': 'bg-pink-50 text-pink-700',
  'Warm': 'bg-amber-50 text-amber-700',
  'Clean': 'bg-sky-50 text-sky-700',
  'Corporate': 'bg-slate-100 text-slate-700',
  'Creative': 'bg-fuchsia-50 text-fuchsia-700',
};

const FEATURE_BULLETS = [
  { icon: 'ri-layout-line', label: 'Responsive layout' },
  { icon: 'ri-palette-line', label: 'Fully customizable' },
  { icon: 'ri-seo-line', label: 'SEO optimized' },
  { icon: 'ri-speed-line', label: 'Fast load times' },
  { icon: 'ri-shield-check-line', label: 'Secure & reliable' },
  { icon: 'ri-device-line', label: 'Mobile-first design' },
];

export default function TemplatePreviewModal({ template, onClose }: TemplatePreviewModalProps) {
  const gradientClass = CATEGORY_COLORS[template.category] ?? 'from-violet-500 to-cyan-500';

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-6xl max-h-[92vh] bg-white rounded-2xl overflow-hidden flex flex-col">

        {/* Header bar */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${gradientClass} flex items-center justify-center`}>
              <i className="ri-layout-grid-line text-white text-sm"></i>
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">{template.name}</h2>
              <p className="text-xs text-gray-500">{template.category}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400 hidden sm:block">Press Esc to close</span>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-1 overflow-hidden">

          {/* Left — Preview */}
          <div className="flex-1 bg-gray-50 overflow-auto relative flex items-start justify-center p-6">
            {/* Browser chrome mockup */}
            <div className="w-full max-w-3xl rounded-xl overflow-hidden border border-gray-200 shadow-lg">
              {/* Browser bar */}
              <div className="bg-gray-200 px-4 py-2.5 flex items-center gap-3">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="flex-1 bg-white rounded-md px-3 py-1 flex items-center gap-2">
                  <i className="ri-lock-line text-gray-400 text-xs"></i>
                  <span className="text-xs text-gray-500 font-mono">mywebsite.openclaw.ai</span>
                </div>
                <i className="ri-refresh-line text-gray-500 text-sm"></i>
              </div>
              {/* Screenshot */}
              <div className="w-full" style={{ height: 420 }}>
                <img
                  src={template.image}
                  alt={template.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Badge overlay */}
            {template.badge && (
              <div className={`absolute top-8 left-8 px-3 py-1 text-white text-xs font-semibold rounded-full shadow ${
                template.badge === 'Popular' ? 'bg-amber-500' : 'bg-emerald-500'
              }`}>
                {template.badge === 'Popular' ? (
                  <><i className="ri-fire-line mr-1"></i>Popular</>
                ) : (
                  <><i className="ri-sparkle-line mr-1"></i>New</>
                )}
              </div>
            )}
          </div>

          {/* Right — Details panel */}
          <div className="w-80 flex-shrink-0 border-l border-gray-100 flex flex-col overflow-y-auto">
            <div className="p-6 flex flex-col gap-6 flex-1">

              {/* Title & description */}
              <div>
                <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r ${gradientClass} text-white text-xs font-medium mb-3`}>
                  <i className="ri-folder-line text-xs"></i>
                  {template.category}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{template.name}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{template.description}</p>
              </div>

              {/* Tags */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Style Tags</p>
                <div className="flex flex-wrap gap-1.5">
                  {template.tags.map((tag) => (
                    <span
                      key={tag}
                      className={`px-2.5 py-1 text-xs font-medium rounded-full ${TAG_STYLES[tag] ?? 'bg-gray-100 text-gray-700'}`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Features */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">What&apos;s included</p>
                <ul className="space-y-2">
                  {FEATURE_BULLETS.map(({ icon, label }) => (
                    <li key={label} className="flex items-center gap-2.5 text-sm text-gray-700">
                      <div className={`w-6 h-6 flex items-center justify-center rounded-md bg-gradient-to-br ${gradientClass} bg-opacity-10`}>
                        <i className={`${icon} text-xs text-gray-600`}></i>
                      </div>
                      {label}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Date added */}
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <i className="ri-calendar-line"></i>
                Added {new Date(template.dateAdded).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
            </div>

            {/* CTA sticky footer */}
            <div className="p-5 border-t border-gray-100 flex flex-col gap-3 bg-white flex-shrink-0">
              <button
                className={`w-full py-3 rounded-xl bg-gradient-to-r ${gradientClass} text-white font-semibold text-sm flex items-center justify-center gap-2 hover:opacity-90 transition-opacity shadow-lg cursor-pointer whitespace-nowrap`}
              >
                <i className="ri-rocket-line"></i>
                Start Building
              </button>
              <button
                onClick={onClose}
                className="w-full py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Browse More Templates
              </button>
              <p className="text-center text-xs text-gray-400">
                Powered by <span className="font-semibold text-gray-500">openclaw.ai</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
